import React from "react"

import { Building2, Edit, MapPin, Plus } from "lucide-react"

import { TableEmptyRow } from "@/components/shared/EmptyState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TabsContent } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { Customer, Location } from "@/types/collections"

import { getCustomerTypeColor, getStatusColor } from "./badgeColors"

interface CustomersTabProps {
  /** Already narrowed by the directory search. */
  customers: Customer[]
  locationsFor: (customerId: string) => Location[]
  onOpenShipper: (customer: Customer) => void
  onNew: () => void
  onEdit: (customer: Customer) => void
}

const CustomersTab: React.FC<CustomersTabProps> = ({
  customers,
  locationsFor,
  onOpenShipper,
  onNew,
  onEdit,
}) => (
  <TabsContent value="customers" className="m-0 h-full">
    <div className="bg-white rounded-lg border border-palette-jungleGreen200">
      <div className="flex items-center justify-between p-4 border-b border-palette-jungleGreen200">
        <h3 className="font-medium text-palette-jungleGreen900">All Customers</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onNew}
          className="h-7 bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen000 gap-1.5 text-xs"
        >
          <Plus size={12} />
          New Customer
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-palette-jungleGreen000/50">
            <TableHead className="text-xs">Name</TableHead>
            <TableHead className="text-xs">Account ID</TableHead>
            <TableHead className="text-xs">Type</TableHead>
            <TableHead className="text-xs">Locations</TableHead>
            <TableHead className="text-xs">Status</TableHead>
            <TableHead className="text-xs text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.length === 0 ? (
            <TableEmptyRow colSpan={6}>No customers found</TableEmptyRow>
          ) : (
            customers.map((customer) => {
              const customerLocations = locationsFor(customer.id)
              return (
                <TableRow key={customer.id} className="hover:bg-palette-jungleGreen000">
                  <TableCell className="font-medium text-sm">
                    <button
                      type="button"
                      onClick={() => onOpenShipper(customer)}
                      disabled={customerLocations.length === 0}
                      title={`Open ${customer.name}`}
                      className="flex items-center gap-2 text-left text-blue-600 hover:underline disabled:text-palette-jungleGreen700 disabled:no-underline disabled:cursor-default"
                    >
                      <Building2 size={14} className="text-palette-jungleGreen400" />
                      {customer.name}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-palette-jungleGreen600 font-mono">
                    {customer.account_id}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "text-xs pointer-events-none",
                        getCustomerTypeColor(customer.customer_type),
                      )}
                    >
                      {customer.customer_type === "pudo"
                        ? "PUDO"
                        : customer.customer_type.charAt(0).toUpperCase() +
                          customer.customer_type.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={customerLocations.length === 0}
                      onClick={() => onOpenShipper(customer)}
                      className="h-6 gap-1 text-xs bg-palette-jungleGreen000 font-normal"
                    >
                      <MapPin size={10} />
                      {customerLocations.length}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "px-2 py-0.5 text-xs rounded border",
                        getStatusColor(customer.status),
                      )}
                    >
                      {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${customer.name}`}
                      onClick={() => onEdit(customer)}
                    >
                      <Edit size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  </TabsContent>
)

export default CustomersTab
